import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useLazyQuery } from '@apollo/client';
import { handleGraphQLError } from './utils';

function useNRLazyQuery({ query, options, qlObjKey, transformData, defaultValue, hideError = false }) {
  const [isCanceled, setIsCanceled] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState(defaultValue);

  const [customLoading, setCustomLoading] = useState(false);

  const [makeCall, { loading, error, data, called, startPolling, stopPolling, previousData }] = useLazyQuery(query, {
    ...options,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    skip: options.skipPollAttempt && options.skipPollAttempt(),
    onCompleted: data => {
      try {
        if (options?.onCompleted) {
          options.onCompleted(data);
        }
      } finally {
        setCustomLoading(false);
      }
    },
    onError: error => {
      setCustomLoading(false);
      if (options?.onError) {
        options.onError(error);
      }
    }
  });

  const handleMakeCall = useCallback(
    options => {
      setIsCanceled(false);
      setCustomLoading(true);
      setResult(defaultValue);
      makeCall(options);
    },
    [makeCall]
  );

  function onCompleted(data) {
    if (!isCanceled && data && data[qlObjKey]) {
      if (data[qlObjKey].items) {
        setResult(!!transformData ? transformData(data[qlObjKey].items) : data[qlObjKey].items);
      } else {
        setResult(!!transformData ? transformData(data[qlObjKey]) : data[qlObjKey]);
      }
      if (data[qlObjKey].total || data[qlObjKey].total === 0) {
        setTotal(data[qlObjKey].total);
      }
      if (data[qlObjKey].pageInfo) {
        if (data[qlObjKey]?.pageInfo?.totalSize != null) {
          setTotal(data[qlObjKey].pageInfo.totalSize);
        }
        if (data[qlObjKey]?.pageInfo?.totalCount != null) {
          setTotal(data[qlObjKey].pageInfo.totalCount);
        }
      }
      if (data[qlObjKey].totalCount != null) {
        setTotal(data[qlObjKey].totalCount);
      }
    }
  }

  const handleCancel = useCallback(() => {
    setIsCanceled(true); // Mark request as canceled
    setCustomLoading(false);
  }, []);

  // Single sync point for local result state. Apollo's onCompleted does not fire
  // on refetchQueries or other cache updates, so we sync once a fetch settles
  // (loading === false) with data present. This covers initial load, refetches,
  // re-executes that resolve to an identical data reference, and partial data
  // returned alongside errors (errorPolicy: 'all').
  useEffect(() => {
    if (data && !loading) {
      onCompleted(data);
    }
  }, [data, loading]);

  useEffect(() => {
    if (error) {
      if (!hideError) {
        handleGraphQLError({
          error,
          enqueueSnackbar,
          onFinally: () => setCustomLoading(false)
        });
      }
    }
  }, [error]);

  return [result, customLoading, called, handleMakeCall, total, error, startPolling, stopPolling, handleCancel, isCanceled];
}

export default useNRLazyQuery;
